/**
 * Cross-reference resolution.
 *
 * Walks every entity field and classifies the links it expresses:
 *  - **embed** — the field type *is* an entity (`WashSaleRisk`, `Position[]`).
 *  - **fk**    — the field name is `xxxId` / `xxxIds` and the `xxx` resolves to
 *               an entity (`holdingId` → `Holding`, `accountIds` → `Account`).
 *  - **enum**  — the field type is a known `type` alias; recorded on the field
 *               but not added as a graph edge (enums are not entities).
 *
 * Fields are mutated in place with `referencesEntity` / `referencesEnum`; the
 * function returns the entity-to-entity {@link Reference} edges.
 *
 * @see docs/impl/147-brisarx-atlas-design.html §7.3 step 2
 */
import type { Entity, Reference } from '../types.js';

const IDENTIFIER_RE = /^[A-Za-z_]\w*$/;
const ENTITY_NAME_RE = /^[A-Z]\w*$/;

/**
 * Extract the entity name from an `EntityRef<'Foo'>` branded-string type.
 * Handles array wrappers: `EntityRef<'Foo'>[]`, `Array<EntityRef<'Foo'>>`.
 *
 * Returns the quoted string literal (the entity name) or `null` for all other
 * type expressions. Used to create typed FK edges that preserve `kind: 'fk'`
 * rather than treating the field as an embedded document.
 */
export function extractEntityRefTarget(type: string): string | null {
  let t = type.trim();
  while (t.endsWith('[]')) t = t.slice(0, -2).trim();
  const arrayWrap = t.match(/^(?:Readonly)?Array<(.+)>$/);
  if (arrayWrap?.[1]) t = arrayWrap[1].trim();
  return t.match(/^[A-Za-z_]\w*<'([A-Za-z_]\w*)'>$/)?.[1] ?? null;
}

/** Reduce a declared type to a single base identifier, or null when it isn't one. */
export function baseTypeName(type: string): string | null {
  let t = type.trim();
  // X[] array suffix
  while (t.endsWith('[]')) t = t.slice(0, -2).trim();
  // Array<X> / ReadonlyArray<X> wrappers
  const arrayWrap = t.match(/^(?:Readonly)?Array<(.+)>$/);
  if (arrayWrap?.[1]) t = arrayWrap[1].trim();
  // Anything still carrying generics, unions, objects, etc. is not a plain ref.
  if (!IDENTIFIER_RE.test(t)) return null;
  return t;
}

/**
 * Resolve an `xxxId` / `xxxIds` field name to an entity id by matching the
 * longest entity whose lowercased name is a suffix of the base — so
 * `investmentAccountId` resolves to `InvestmentAccount`, not `Account`.
 *
 * Tie-break: when two candidates have equal-length names the lexicographically
 * smaller entity id wins, making the result stable regardless of Set iteration
 * order. Ties are not expected in practice because unique entity names produce
 * unique lowercased suffix strings of any given length.
 */
function resolveFkTarget(fieldName: string, entityIds: Set<string>): string | null {
  const m = fieldName.match(/^(.*?)(Ids?)$/);
  if (!m || !m[1]) return null;
  const base = m[1].toLowerCase();
  let best: string | null = null;
  for (const id of entityIds) {
    const lower = id.toLowerCase();
    if (base === lower || base.endsWith(lower)) {
      if (best === null || id.length > best.length || (id.length === best.length && id < best)) {
        best = id;
      }
    }
  }
  return best;
}

/**
 * Classify every field's references. Mutates `entities[].fields[]` and returns
 * the entity-to-entity reference edges.
 */
export function resolveCrossRefs(entities: Entity[], enumNames: string[]): Reference[] {
  const entityIds = new Set(entities.map((e) => e.id));
  const domainById = new Map(entities.map((e) => [e.id, e.domain]));
  const enums = new Set(enumNames);
  const references: Reference[] = [];

  for (const entity of entities) {
    for (const field of entity.fields) {
      const base = baseTypeName(field.type);

      // 0. EntityRef<'Foo'> typed FK — branded-string generic; kind stays 'fk'
      //    so the viewer renders it as a reference, not an embedded document.
      const entityRefTarget = extractEntityRefTarget(field.type);
      if (entityRefTarget && entityIds.has(entityRefTarget)) {
        field.referencesEntity = entityRefTarget;
        references.push({
          from: entity.id,
          to: entityRefTarget,
          fromField: field.name,
          kind: 'fk',
          optional: field.optional,
          crossDomain: entity.domain !== domainById.get(entityRefTarget),
        });
        continue;
      }

      // 1. embed — the field's type is itself an entity.
      if (base && entityIds.has(base)) {
        field.referencesEntity = base;
        references.push({
          from: entity.id,
          to: base,
          fromField: field.name,
          kind: 'embed',
          optional: field.optional,
          crossDomain: entity.domain !== domainById.get(base),
        });
        continue;
      }

      // 2. enum — the field's type is a known type alias.
      if (base && enums.has(base)) {
        field.referencesEnum = base;
        continue;
      }

      // 3. fk — the field name resolves to an entity via an id suffix.
      // Guard: skip own-identity self-loops — e.g. `transactionId` on `Transaction`
      // resolves to itself (a parser artifact) but `parentPositionId` on `Position`
      // is a legitimate tree-pointer and must be kept. Own-identity means the FK
      // base name *exactly equals* the entity name (case-insensitive).
      if (ENTITY_NAME_RE.test(base ?? '') === false) {
        const target = resolveFkTarget(field.name, entityIds);
        if (target) {
          const fkBaseLower = field.name.match(/^(.*?)(Ids?)$/)?.[1]?.toLowerCase() ?? '';
          const isSelfIdentity = target === entity.id && fkBaseLower === entity.id.toLowerCase();
          if (!isSelfIdentity) {
            field.referencesEntity = target;
            references.push({
              from: entity.id,
              to: target,
              fromField: field.name,
              kind: 'fk',
              optional: field.optional,
              crossDomain: entity.domain !== domainById.get(target),
            });
          }
        }
      }
    }
  }
  return references;
}
